import { Component, Input, ViewEncapsulation } from '@angular/core';
import { AccountService } from 'src/app/services/account.service';
import { ArticleService } from 'src/app/services/article.service';
import { DiseaseService } from 'src/app/services/disease.service';
import { DiagnosisItemType } from 'shared/salesforce-types';
import { Article, Tag, Disease } from 'shared/types';
import { Page, Section, SubSection, SubSubsection } from 'shared/location-enums';
import { CauseSnippets } from '../../../common/cause/cause-functions';
import { hasDiseaseSpecificPatientAdvocacyGroup } from '../../../common/organizations/patient-advocacy-groups';

interface CategoriesLastWord extends Tag {
    nameCuratedAllButLast: string;
    nameCuratedLast: string;
}

@Component({
    selector: 'disease-at-a-glance-snippets',
    templateUrl: './disease-at-a-glance-snippets.component.html',
    styleUrls: ['./disease-at-a-glance-snippets.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class DiseaseAtAGlanceSnippetsComponent {
    @Input() nestedHeading = false;

    // Feature flags
    public showPopulationEstimate = false; // Set to true to show the population estimate bullet

    public mainTitle = '';
    public mainSummary = '';

    public populationEstimateTitle = '';
    public populationEstimateBody = '';
    public populationEstimateBodyLastWord = '';
    public populationEstimateTooltip = '';

    public ageAtOnsetTitle = '';
    public ageAtOnsetBody = '';

    public causeArticleTitle = '';
    public causeArticleBody = '';

    public patientOrgTitle = '';
    public patientOrgBody = '';

    public newbornTitle = '';
    public newbornBody = '';
    public newbornBodyLastWord = '';
    public newbornTooltip = '';

    public categoriesTitle = 'Categories:';
    public categories: CategoriesLastWord[] = [];

    private articleService: ArticleService;
    private accountService: AccountService;
    private diseaseService: DiseaseService;

    constructor(articleService: ArticleService, accountService: AccountService, diseaseService: DiseaseService) {
        this.articleService = articleService;
        this.accountService = accountService;
        this.diseaseService = diseaseService;

        this.categories = (diseaseService.disease.diseaseCategoriesWithDescriptions as CategoriesLastWord[]) || [];

        // process categories text split for last words
        this.categories = this.categories.map(cat => {
            const arrNameCuratedWords = (cat.nameCurated || cat.name || '').split(' ');
            cat.nameCuratedLast = arrNameCuratedWords.pop();
            cat.nameCuratedAllButLast = arrNameCuratedWords.join(' ');
            return cat;
        });

        /* Main Article */
        const mainArticle = this.articleService.getOne(Page.about, Section.diseaseAtAGlance, SubSection.introSnippet, undefined, undefined, false);
        if (mainArticle) {
            this.mainTitle = mainArticle.title.replace('${DiseaseName}', diseaseService.disease.name);

            // Remove any line breaks to ensure correct spacing
            this.mainSummary = mainArticle.bodyText.replace('${DiseaseName}', diseaseService.disease.name).replace(/<br>/g, '');
        }

        /* Population Estimate */
        const populationArticle = this.articleService.getOne(
            Page.about,
            Section.diseaseAtAGlance,
            SubSection.estimateSnippet,
            undefined,
            undefined,
            this.isPopulationNull(diseaseService.disease),
        );
        const populationTooltip = this.articleService.getOne(Page.about, Section.diseaseAtAGlance, SubSection.epidemiology, undefined, undefined, false, SubSubsection.usaInfo);

        if (populationArticle) {
            this.populationEstimateTooltip = populationTooltip.bodyText;
            this.populationEstimateTitle = populationArticle.title;
            const fullBody = populationArticle.bodyText.replace('${CuratedUSAEstimate}', diseaseService.disease.caseEstimateUsa).split(' ');
            this.populationEstimateBodyLastWord = fullBody.pop();
            this.populationEstimateBody = fullBody.join(' ');
        }

        /* Age At Onset */
        const ageAtOnsetArticle = this.articleService.getOne(
            Page.about,
            Section.diseaseAtAGlance,
            SubSection.ageAtOnsetSnippet,
            undefined,
            undefined,
            this.isAgeAtOnsetNull(diseaseService.disease),
        );
        if (ageAtOnsetArticle) {
            this.ageAtOnsetTitle = ageAtOnsetArticle.title;
            this.ageAtOnsetBody = ageAtOnsetArticle.bodyText.replace('${AgeAtOnsetSnippetText}', diseaseService.disease.ageAtOnsetSnippet);
        }

        /* Cause */
        const causeArticle: Article = this.getCauseSnippetKA(diseaseService.disease);
        if (causeArticle) {
            this.causeArticleTitle = causeArticle.title;
            this.causeArticleBody = causeArticle.bodyText;
        }

        /* Patient Organizations */
        const patientOrgArticle = this.articleService.getOne(Page.about, Section.diseaseAtAGlance, SubSection.pagSnippet, undefined, undefined, this.isPatientOrgNull());

        if (patientOrgArticle) {
            this.patientOrgTitle = patientOrgArticle.title;
            this.patientOrgBody = patientOrgArticle.bodyText;
        }

        /* Newborn Screening */
        const newbornArticle = this.articleService
            .getArticles(Page.about, Section.diseaseAtAGlance, SubSection.newbornScreenSnippet, undefined, undefined, this.isNewbornNull(diseaseService.disease))
            .filter(article => !article.subSubsection)
            .pop(); // Grab the article with no subsubsection
        const newbornTooltip = this.articleService.getOne(
            Page.about,
            Section.diseaseAtAGlance,
            SubSection.newbornScreenSnippet,
            undefined,
            undefined,
            false,
            SubSubsection.newbornScreenDesc,
        );

        if (newbornArticle) {
            this.newbornTitle = newbornArticle.title;

            const fullBody = newbornArticle.bodyText.split(' ');
            this.newbornBodyLastWord = fullBody.pop();
            this.newbornBody = fullBody.join(' ');

            this.newbornTooltip = newbornTooltip.bodyText;
        }
    }

    isPopulationNull(disease: Disease): boolean {
        return !disease.caseEstimateUsa;
    }

    isAgeAtOnsetNull(disease: Disease): boolean {
        return !disease.ageAtOnsetSnippet;
    }

    isPatientOrgNull(): boolean {
        return !hasDiseaseSpecificPatientAdvocacyGroup(this.accountService.getAccounts(), this.diseaseService.disease);
    }

    isNewbornNull(disease: Disease): boolean {
        const newbornDiagnosis = (disease.diagnosis || []).filter(diagnosis => diagnosis.Type__c === DiagnosisItemType.NEWBORN);

        return !newbornDiagnosis.length;
    }

    getCauseSnippetKA(disease: Disease): Article {
        const causeArticles = this.articleService.getArticles(Page.about, Section.diseaseAtAGlance, SubSection.causeSnippet, undefined, undefined, false);
        const nullKa = this.articleService.getOne(Page.about, Section.diseaseAtAGlance, SubSection.causeSnippet, undefined, undefined, true);
        return CauseSnippets(disease, causeArticles, nullKa);
    }
}
